import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { useTheme } from "@/context/ThemeContext";
import { 
  ArrowLeft,
  Check,
  Coins,
  Award,
  Crown,
  Star,
  Zap,
  Trophy,
  Sparkles
} from "lucide-react";

const TitlesPage = ({ user }) => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [categories, setCategories] = useState([]);
  const [ownedTitles, setOwnedTitles] = useState([]);
  const [activeTitle, setActiveTitle] = useState(null);
  const [balance, setBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);
  const [settingActive, setSettingActive] = useState(null);

  const [titleDetails, setTitleDetails] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [titlesRes, myTitlesRes, balanceRes] = await Promise.all([
        axios.get(`${API}/titles`),
        axios.get(`${API}/titles/my`),
        axios.get(`${API}/credits/balance`)
      ]);
      
      setCategories(titlesRes.data.categories);
      setOwnedTitles(myTitlesRes.data.owned || []);
      setActiveTitle(myTitlesRes.data.active);
      setTitleDetails(myTitlesRes.data.details || []);
      setBalance(balanceRes.data.balance);
    } catch (error) {
      console.error("Veri yüklenemedi:", error);
      toast.error("Veriler yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async (titleId) => {
    setPurchasing(titleId);
    try {
      const response = await axios.post(`${API}/titles/purchase`, {
        titleId: titleId
      });
      
      if (response.data.success) {
        toast.success(response.data.message);
        if (!ownedTitles.includes(titleId)) {
          setOwnedTitles(prev => [...prev, titleId]);
        }
        setTitleDetails(prev => [
          ...prev.filter(d => d.id !== titleId),
          { id: titleId, purchasedAt: response.data.purchasedAt, expiresAt: response.data.expiresAt }
        ]);
        const balanceRes = await axios.get(`${API}/credits/balance`);
        setBalance(balanceRes.data.balance);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Satın alma başarısız");
    } finally {
      setPurchasing(null);
    }
  };

  const getRemainingDays = (titleId) => {
    const detail = titleDetails.find(d => d.id === titleId);
    if (!detail || !detail.expiresAt) return null;
    const now = new Date();
    const expires = new Date(detail.expiresAt);
    const diff = Math.ceil((expires - now) / (1000 * 60 * 60 * 24));
    return diff > 0 ? diff : 0;
  };

  const handleSetActive = async (titleId) => {
    setSettingActive(titleId);
    try {
      const response = await axios.post(`${API}/titles/set-active`, {
        titleId: titleId
      });
      
      if (response.data.success) {
        toast.success(response.data.message);
        setActiveTitle(titleId === "" ? null : titleId);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "İşlem başarısız");
    } finally {
      setSettingActive(null);
    }
  };

  const getCategoryGradient = (categoryId) => {
    const gradients = {
      standard: "from-amber-500 to-orange-500",
      premium: "from-blue-500 to-indigo-500",
      elite: "from-yellow-400 to-amber-500",
      legendary: "from-purple-500 to-pink-500"
    };
    return gradients[categoryId] || gradients.standard;
  };

  const getCategoryBg = (categoryId) => {
    const bgs = {
      standard: "bg-amber-50 border-amber-200",
      premium: "bg-blue-50 border-blue-200",
      elite: "bg-yellow-50 border-yellow-200",
      legendary: "bg-purple-50 border-purple-200"
    };
    return bgs[categoryId] || bgs.standard;
  };

  const getCategoryIcon = (categoryId) => {
    const icons = {
      standard: Zap,
      premium: Star,
      elite: Crown,
      legendary: Trophy
    };
    return icons[categoryId] || Award;
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-[#1f2224]' : ''}`}>
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${isDark ? 'bg-[#1f2224]' : 'bg-gradient-to-br from-slate-50 via-white to-purple-50'}`}>
      {/* Header */}
      <div className={`${isDark ? 'bg-[#2a2d2f]' : 'bg-gradient-to-r from-purple-600 to-indigo-600'} text-white`}>
        <div className="container mx-auto px-4 py-8">
          <Button 
            variant="ghost" 
            className="text-white hover:bg-white/20 mb-4"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Geri
          </Button>
          
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-3">
                <Award className="h-8 w-8" />
                Bireysel Ünvanlar
              </h1>
              <p className={`mt-1 ${isDark ? 'text-gray-300' : 'text-purple-100'}`}>
                Profilini öne çıkar, ünvan sahibi ol!
              </p>
            </div>
            
            <div className="flex gap-4">
              <div className={`backdrop-blur-sm rounded-xl px-6 py-4 ${isDark ? 'bg-[#3a3d40]' : 'bg-white/20'}`}>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-purple-100'}`}>Mevcut Bakiye</p>
                <p className="text-2xl font-bold flex items-center gap-2">
                  <Coins className="h-5 w-5" />
                  {balance}
                </p>
              </div>
              <div className={`backdrop-blur-sm rounded-xl px-6 py-4 ${isDark ? 'bg-[#3a3d40]' : 'bg-white/20'}`}>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-purple-100'}`}>Sahip Olduğun</p>
                <p className="text-2xl font-bold flex items-center gap-2">
                  <Trophy className="h-5 w-5" />
                  {ownedTitles.length}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Aktif Ünvan */}
        {activeTitle && (
          <Card className={`mb-8 border-2 ${isDark ? 'bg-[#2a2d2f] border-purple-800' : 'border-purple-300 bg-purple-50'}`}>
            <CardHeader className="pb-2">
              <CardTitle className={`text-lg flex items-center gap-2 ${isDark ? 'text-purple-400' : ''}`}>
                <Sparkles className="h-5 w-5 text-purple-600" />
                Aktif Ünvanın
              </CardTitle>
            </CardHeader>
            <CardContent>
              {(() => {
                const title = categories
                  .flatMap(c => c.titles)
                  .find(t => t.id === activeTitle);
                if (!title) return null;
                return (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-3xl">{title.icon}</span>
                      <div>
                        <p className="font-bold text-lg">{title.name}</p>
                        <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                          {title.description}
                          {getRemainingDays(activeTitle) !== null && (
                            <span className="ml-2 text-purple-600 dark:text-purple-400 font-medium">
                              ({getRemainingDays(activeTitle)} gün kaldı)
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                    <Button 
                      variant="outline"
                      onClick={() => handleSetActive("")}
                      disabled={settingActive === ""}
                    >
                      Kaldır
                    </Button>
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        )}

        {/* Kategoriler */}
        <div className="space-y-8">
          {categories.map((category) => {
            const CategoryIcon = getCategoryIcon(category.id);
            const gradient = getCategoryGradient(category.id);
            
            return (
              <div key={category.id}>
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center`}>
                    <CategoryIcon className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h2 className={`text-xl font-bold flex items-center gap-2 ${isDark ? 'text-white' : ''}`}>
                      {category.icon} {category.name}
                    </h2>
                    <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      {category.id === "standard" && "30 kredi / ay"}
                      {category.id === "premium" && "50 kredi / ay"}
                      {category.id === "elite" && "75 kredi / ay"}
                      {category.id === "legendary" && "100 kredi / ay"}
                    </p>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {category.titles.map((title) => {
                    const isOwned = ownedTitles.includes(title.id);
                    const isActive = activeTitle === title.id;
                    
                    return (
                      <Card 
                        key={title.id}
                        className={`relative transition-all hover:shadow-lg ${
                          isOwned 
                            ? isDark ? 'bg-[#2a2d2f] border-2 border-emerald-800' : 'bg-emerald-50 border-2 border-emerald-200'
                            : isDark ? 'bg-[#2a2d2f] border-[#3a3d40]' : ''
                        } ${isActive ? 'ring-2 ring-purple-500' : ''} ${!isOwned ? 'hover:-translate-y-1' : ''}`}
                      >
                        {isOwned && (
                          <div className="absolute top-2 right-2">
                            <Badge className="bg-emerald-500 text-white">
                              <Check className="h-3 w-3 mr-1" />
                              {getRemainingDays(title.id) !== null ? `${getRemainingDays(title.id)} gün` : 'Sahip'}
                            </Badge>
                          </div>
                        )}
                        
                        <CardHeader className="pb-2">
                          <div className="flex items-center gap-3">
                            <span className="text-3xl">{title.icon}</span>
                            <div>
                              <CardTitle className={`text-lg ${isDark ? 'text-white' : ''}`}>{title.name}</CardTitle>
                              <CardDescription className={`text-xs ${isDark ? 'text-gray-400' : ''}`}>
                                {title.description}
                              </CardDescription>
                            </div>
                          </div>
                        </CardHeader>
                        
                        <CardContent>
                          {isOwned ? (
                            <Button 
                              className={`w-full ${isActive 
                                ? 'bg-purple-600 hover:bg-purple-700' 
                                : 'bg-gray-600 hover:bg-gray-700'
                              }`}
                              onClick={() => handleSetActive(isActive ? "" : title.id)}
                              disabled={settingActive === title.id}
                            >
                              {settingActive === title.id ? (
                                <span className="flex items-center gap-2">
                                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                                </span>
                              ) : isActive ? (
                                <span className="flex items-center gap-2">
                                  <Sparkles className="h-4 w-4" />
                                  Aktif
                                </span>
                              ) : (
                                <span className="flex items-center gap-2">
                                  <Award className="h-4 w-4" />
                                  Aktif Yap
                                </span>
                              )}
                            </Button>
                          ) : (
                            <Button 
                              className={`w-full bg-gradient-to-r ${gradient} hover:opacity-90 text-white`}
                              onClick={() => handlePurchase(title.id)}
                              disabled={purchasing === title.id || balance < title.price}
                            >
                              {purchasing === title.id ? (
                                <span className="flex items-center gap-2">
                                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                                  İşleniyor...
                                </span>
                              ) : (
                                <span className="flex items-center gap-2">
                                  <Coins className="h-4 w-4" />
                                  {title.price} Kredi / Ay
                                </span>
                              )}
                            </Button>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
        
        {/* Kredi yetersiz uyarısı */}
        {balance < 30 && (
          <Card className="mt-8 bg-amber-50 border-amber-200">
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Coins className="h-6 w-6 text-amber-600" />
                  <div>
                    <p className="font-semibold text-amber-800">Kredin yetersiz</p>
                    <p className="text-sm text-amber-600">Ünvan satın almak için kredi yükle</p>
                  </div>
                </div>
                <Button 
                  onClick={() => navigate("/credits")}
                  className="bg-amber-600 hover:bg-amber-700"
                >
                  Kredi Al
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default TitlesPage;
